import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import { Autoplay, Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { useRecommendedProducts } from '../../hooks/useRecommendedProducts'
import {
  removeFromCart,
  selectCartItems,
  selectCartStats,
  updateQuantity,
} from '../../store/slices/cartSlice'
import { useCart } from '../../hooks/useCart'
import { CartItem } from '../../types/cart'
import { Product } from '../../types/product'
import {
  buildProductCartMeta,
  getCartItemUnitPrice,
  getDefaultVariant,
  getDiscountPercent,
  getEffectivePrice,
  getProductDisplayWeight,
  hasCartItemDiscount,
  hasCatalogDiscount,
} from '../../utils/cartUtils'
import Image from '../ui/Image/Image'
import MainButton from '../ui/MainButton/MainButton'
import { DiscountBadge } from '../ui/ProductMarkers/ProductMarkers'
import styles from './CartSidebar.module.css'

interface CartSidebarProps {
  isOpen: boolean
  onClose: () => void
}

const isMobile = window.innerWidth < 1024

// Константи для відображення
const DISPLAY_VALUES: Record<string, string> = {
  grain: 'у зерні',
  v60: 'V-60',
  aeropress: 'Аеропрес',
  filter: 'Фільтр',
  geyser: 'Гейзер',
  jezva: 'Джезва',
  espresso: 'еспресо',
  '250g': '250 г',
  '500g': '500 г',
  '1kg': '1 кг',
  light: 'світла',
  medium: 'середня',
  dark: 'темна',
  fine: 'дрібний',
  medium_grind: 'середній',
  coarse: 'грубий',
}

// Компонент для відображення товару в корзині
const CartItemComponent: React.FC<{
  item: CartItem
  onQuantityChange: (item: CartItem, quantity: number) => void
  onRemove: (item: CartItem) => void
}> = React.memo(({ item, onQuantityChange, onRemove }) => {
  const unitPrice = getCartItemUnitPrice(item)
  const hasDiscount = hasCartItemDiscount(item)

  const getMetaDataValue = useCallback(
    (key: string): string => {
      const meta = item.meta_data?.find((m: any) => m.key === key)
      return meta?.value || ''
    },
    [item.meta_data],
  )

  const getDisplayValue = useCallback((value: string): string => {
    return DISPLAY_VALUES[value] || value
  }, [])

  const handleQuantityChange = useCallback(
    (delta: number) => {
      const newQuantity = item.quantity + delta
      if (newQuantity <= 0) {
        onRemove(item)
      } else {
        onQuantityChange(item, newQuantity)
      }
    },
    [item, onQuantityChange, onRemove],
  )

  return (
    <div className={styles.cartItem} data-testid={`cart-item-${item.id}`}>
      <div className={styles.topItem}>
        <div className={styles.itemImage}>
          <img
            src={item.image}
            alt={item.name}
            width={80}
            height={80}
            loading="lazy"
            onError={e => {
              const target = e.target as HTMLImageElement
              target.src = '/images/product.png'
            }}
          />
        </div>

        <div className={styles.itemInfo}>
          <h3 className={styles.itemName} title={item.name}>
            {item.name}
          </h3>
          {/* <div className={styles.itemDetails}>
            <span>
              <span className={styles.itemDetailsLabel}>Фасування:</span>
              <span>{getDisplayValue(getMetaDataValue('packageWeight') || getMetaDataValue('packageSize'))}</span>
            </span>
            <span>
              <span className={styles.itemDetailsLabel}>Обсмажка:</span>
              <span>{getDisplayValue(getMetaDataValue('roastType'))}</span>
            </span>
            <span>
              <span className={styles.itemDetailsLabel}>Помел:</span>
              <span>{getDisplayValue(getMetaDataValue('grindType'))}</span>
            </span>
          </div> */}
          {isMobile && (
            <div className={styles.itemPrice}>
              {hasDiscount ? (
                <div className={styles.priceDiscount}>
                  <div className={styles.discountPriceLine}>
                    <span className={styles.salePrice}>{unitPrice} ₴</span>
                    <DiscountBadge discount={item.discount_percent} />
                  </div>
                  <span className={styles.originalPrice}>{item.regular_price} ₴</span>
                </div>
              ) : (
                <span className={styles.price}>{unitPrice} ₴</span>
              )}
              <span className={styles.packagingWeight}>
                / {getDisplayValue(getMetaDataValue('packageWeight') || getMetaDataValue('packageSize') || '')} × {item.quantity}
              </span>
            </div>
          )}
        </div>

        {!isMobile && (
          <div className={styles.itemPrice}>
            {hasDiscount ? (
              <div className={styles.priceDiscount}>
                <div className={styles.discountPriceLine}>
                  <span className={styles.salePrice}>{unitPrice} ₴</span>
                  <DiscountBadge discount={item.discount_percent} />
                </div>
                <span className={styles.originalPrice}>{item.regular_price} ₴</span>
              </div>
            ) : (
              <span className={styles.price}>{unitPrice} ₴</span>
            )}
            <span className={styles.packagingWeight}>
              / {getDisplayValue(getMetaDataValue('packageWeight') || getMetaDataValue('packageSize') || '')} × {item.quantity}
            </span>
          </div>
        )}
      </div>

      <div className={styles.itemActions}>
        <div className={styles.quantity} role="group" aria-label="Кількість товару">
          <button
            type="button"
            onClick={() => handleQuantityChange(-1)}
            disabled={item.quantity <= 1}
            aria-label="Зменшити кількість"
            className={styles.quantityButton}
          >
            <svg width="12" height="2" viewBox="0 0 12 2" fill="none">
              <path d="M1 1H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
          <span className={styles.quantityValue} aria-live="polite">
            {item.quantity}
          </span>
          <button
            type="button"
            onClick={() => handleQuantityChange(1)}
            aria-label="Збільшити кількість"
            className={styles.quantityButton}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M6 1V11M1 6H11"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <button
          type="button"
          className={styles.removeButton}
          onClick={() => onRemove(item)}
          aria-label={`Видалити ${item.name} з корзини`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="10"
            height="12"
            viewBox="0 0 10 12"
            fill="none"
          >
            <path
              d="M7.79195 2.625C8.80486 2.62503 9.38367 3.78134 8.77633 4.5918C8.51691 4.93768 8.37596 5.35866 8.37594 5.79102V9C8.37594 10.3117 7.3126 11.375 6.00094 11.375H4.00094C2.68938 11.3749 1.62594 10.3116 1.62594 9V5.79102C1.62591 5.35867 1.48593 4.93768 1.22652 4.5918C0.618914 3.78136 1.19698 2.62511 2.20992 2.625H7.79195ZM4.00094 5C3.72479 5 3.50094 5.22386 3.50094 5.5V8.5C3.50094 8.77614 3.72479 9 4.00094 9C4.27704 8.99996 4.50094 8.77612 4.50094 8.5V5.5C4.50094 5.22388 4.27704 5.00004 4.00094 5ZM6.00094 5C5.72479 5 5.50094 5.22386 5.50094 5.5V8.5C5.50094 8.77614 5.72479 9 6.00094 9C6.27704 8.99996 6.50094 8.77612 6.50094 8.5V5.5C6.50094 5.22388 6.27704 5.00004 6.00094 5Z"
              fill="#BFBFBF"
            />
            <path
              d="M7 3L6.95585 2.86754C6.68362 2.05086 5.91934 1.5 5.05848 1.5H4.94152C4.08066 1.5 3.31638 2.05086 3.04415 2.86754L3 3"
              stroke="#BFBFBF"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          Видалити
        </button>
      </div>
    </div>
  )
})

CartItemComponent.displayName = 'CartItemComponent'

// Компонент для рекомендацій
const RecommendationsSection: React.FC<{
  recommendedProducts: Product[]
  loading: boolean
  error: string | null
  onAddToCart: (product: Product) => void
}> = React.memo(({ recommendedProducts, loading, error, onAddToCart }) => {
  if (loading) {
    return (
      <div className={styles.recommendations}>
        <h3 className={styles.recommendationsTitle}>Разом пасуватиме</h3>
        <div className={styles.recommendationsLoading}>
          <div className={styles.loadingSpinner}></div>
          <p>Завантажуємо рекомендації...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.recommendations}>
        <h3 className={styles.recommendationsTitle}>Разом пасуватиме</h3>
        <div className={styles.recommendationsError}>
          <p>{error}</p>
        </div>
      </div>
    )
  }

  if (recommendedProducts.length === 0) {
    return (
      <div className={styles.recommendations}>
        <h3 className={styles.recommendationsTitle}>Разом пасуватиме</h3>
        <div className={styles.noRecommendations}>
          <p>Поки що немає рекомендацій</p>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.recommendations}>
      <h3 className={styles.recommendationsTitle}>Разом пасуватиме</h3>
      <Swiper
        modules={[Pagination, Navigation, Autoplay]}
        spaceBetween={16}
        slidesPerView={1}
        pagination={{
          clickable: true,
          el: `.${styles.pagination}`,
          bulletClass: styles.dot,
          bulletActiveClass: styles.active,
        }}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
        }}
        className={styles.recommendationsSwiper}
        onError={(error: any) => {
          // Swiper error
        }}
      >
        {recommendedProducts.map(product => {
          const defaultVariant = getDefaultVariant(product.prices || [])
          const effectivePrice = defaultVariant ? getEffectivePrice(defaultVariant) : 0
          const hasDiscount = hasCatalogDiscount(defaultVariant?.discount)

          return (
            <SwiperSlide key={product.id}>
            <div className={styles.recommendationItem}>
              <div className={styles.recommendationImage}>
                <Image
                  mainPicture={product.mainPicture}
                  mainPictureBig={product.mainPictureBig}
                  alt={product.name}
                />
              </div>
                <div className={styles.recommendationInfo}>
                <h4 title={product.name}>{product.name}</h4>
                <div className={styles.recommendationPrice}>
                  <span className={styles.recommendationPrice}>
                    {effectivePrice} ₴
                  </span>
                  {hasDiscount && defaultVariant && (
                    <span className={styles.originalPrice}>{defaultVariant.price} ₴</span>
                  )}
                  <DiscountBadge discount={defaultVariant?.discount} />
                  <span className={styles.recommendationWeight}>
                    {' '}
                    / {getProductDisplayWeight(product, getDefaultVariant(product.prices || []))}
                  </span>
                </div>
              </div>
              <button
                type="button"
                className={styles.addToCartButton}
                onClick={() => onAddToCart(product)}
                aria-label={`Додати ${product.name} до корзини`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M12.0007 9.99998H4.66732L3.66732 3.99998H14.0007L12.0007 9.99998Z"
                    fill="white"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M1.33398 1.99998C1.33398 1.63179 1.63246 1.33331 2.00065 1.33331H3.33398C3.65988 1.33331 3.938 1.56892 3.99158 1.89038L4.23207 3.33331H14.0007C14.2149 3.33331 14.4162 3.43633 14.5415 3.61017C14.6668 3.78402 14.7009 4.0075 14.6331 4.2108L12.6331 10.2108C12.5424 10.483 12.2876 10.6666 12.0007 10.6666H4.66732C4.34142 10.6666 4.0633 10.431 4.00972 10.1096L2.76923 2.66665H2.00065C1.63246 2.66665 1.33398 2.36817 1.33398 1.99998ZM4.45429 4.66665L5.23207 9.33331H11.5201L13.0757 4.66665H4.45429Z"
                    fill="white"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M4.00065 13.3333C4.00065 12.5969 4.5976 12 5.33398 12C6.07036 12 6.66732 12.5969 6.66732 13.3333C6.66732 14.0697 6.07036 14.6666 5.33398 14.6666C4.5976 14.6666 4.00065 14.0697 4.00065 13.3333Z"
                    fill="white"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M10.0007 13.3333C10.0007 12.5969 10.5976 12 11.334 12C12.0704 12 12.6673 12.5969 12.6673 13.3333C12.6673 14.0697 12.0704 14.6666 11.334 14.6666C10.5976 14.6666 10.0007 14.0697 10.0007 13.3333Z"
                    fill="white"
                  />
                </svg>
              </button>
            </div>
            </SwiperSlide>
          )
        })}
      </Swiper>
      <div className={styles.pagination}></div>
    </div>
  )
})

RecommendationsSection.displayName = 'RecommendationsSection'

// Основний компонент CartSidebar
const CartSidebar: React.FC<CartSidebarProps> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch()
  const { addItem } = useCart()
  const navigate = useNavigate()
  const cartItems = useSelector(selectCartItems)
  const cartStats = useSelector(selectCartStats)

  // Стан для анімації
  const [isAnimating, setIsAnimating] = useState(false)

  // Стан для відстеження видалених товарів з таймером
  const [deletedItem, setDeletedItem] = useState<{
    item: CartItem
    timer: number
  } | null>(null)

  // Стан для анімації видалення
  const [isDeleting, setIsDeleting] = useState(false)

  // Ref для збереження початкового часу таймера
  const timerStartTimeRef = useRef<number | null>(null)

  // Ref для прямого доступу до цифри таймера
  const timerNumberRef = useRef<HTMLSpanElement>(null)

  // Отримуємо рекомендовані товари на основі категорій у корзині
  const {
    recommendedProducts,
    loading: loadingRecommended,
    error: recommendedError,
  } = useRecommendedProducts(cartItems, 4)

  // Обробники подій
  const handleQuantityChange = useCallback(
    (item: CartItem, quantity: number) => {
      if (quantity <= 0) {
        dispatch(removeFromCart(item))
      } else {
        const grindType = item.meta_data?.find(meta => meta.key === 'grindType')?.value
        dispatch(
          updateQuantity({
            id: item.id,
            variationId: item.variationId,
            chosenVariantId: item.chosenVariantId,
            grindType,
            quantity,
          }),
        )
      }
    },
    [dispatch],
  )

  const handleRemoveItem = useCallback((item: CartItem) => {
    setDeletedItem({ item, timer: 3 })
  }, [])

  const handleRestoreItem = useCallback(() => {
    if (deletedItem) {
      // Товар вже є в корзині, просто очищуємо стан видалення
      setDeletedItem(null)
      setIsDeleting(false)
      timerStartTimeRef.current = null
    }
  }, [deletedItem])

  const handleAddRecommendedToCart = useCallback(
    (product: Product) => {
      const defaultVariant = getDefaultVariant(product.prices || [])
      const effectivePrice = defaultVariant ? getEffectivePrice(defaultVariant) : 0
      const cartItem: CartItem = {
        id: parseInt(product.id),
        name: product.name,
        price: effectivePrice,
        regular_price: defaultVariant?.price ?? 0,
        sale_price: hasCatalogDiscount(defaultVariant?.discount) ? effectivePrice : undefined,
        discount_percent: getDiscountPercent(defaultVariant?.discount),
        image: product.mainPicture || '/images/product.png',
        sku: product.id,
        quantity: 1,
        chosenVariantId: defaultVariant?.id,
        variants: product.prices,
        meta_data: buildProductCartMeta(product, {
          grindLabel: product.grindType?.label,
          variant: defaultVariant,
        }),
      }
      addItem(cartItem)
    },
    [addItem],
  )

  const handleCheckout = useCallback(() => {
    onClose()
    navigate('/checkout')
  }, [navigate, onClose])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    },
    [onClose],
  )

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true)
      document.body.style.overflow = 'hidden'
    } else {
      setIsAnimating(false)
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  useEffect(() => {
    if (cartItems.length > 0) {
      localStorage.setItem('cart', JSON.stringify(cartItems))
    }
  }, [cartItems])

  useEffect(() => {
    if (deletedItem && !timerStartTimeRef.current) {
      timerStartTimeRef.current = Date.now()

      // Встановлюємо початкову цифру
      if (timerNumberRef.current) {
        timerNumberRef.current.textContent = '3'
      }

      // Таймер для зміни цифр
      let currentNumber = 3
      const numberInterval = setInterval(() => {
        currentNumber -= 1

        if (currentNumber <= 0) {
          clearInterval(numberInterval)
          setIsDeleting(true)
          setTimeout(() => {
            dispatch(removeFromCart(deletedItem.item))
            setDeletedItem(null)
            setIsDeleting(false)
            timerStartTimeRef.current = null
          }, 300)
        } else {
          // Оновлюємо тільки DOM елемент, без setState
          if (timerNumberRef.current) {
            timerNumberRef.current.textContent = currentNumber.toString()
          }
        }
      }, 1000)

      return () => {
        clearInterval(numberInterval)
        timerStartTimeRef.current = null
      }
    }
  }, [deletedItem, dispatch])

  const NotificationTimer = React.memo(() => {
    if (!deletedItem) return null

    return (
      <div className={`${styles.deleteNotification} ${isDeleting ? styles.deleting : ''}`}>
        <div className={styles.timerContainer}>
          <div className={styles.timerCircle}>
            <svg className={styles.timerSvg} viewBox="0 0 36 36">
              <path
                className={styles.timerBackground}
                d="M18 2.0845
                  a 15.9155 15.9155 0 0 1 0 31.831
                  a 15.9155 15.9155 0 0 1 0 -31.831"
              />
              <path
                className={styles.timerProgress}
                d="M18 2.0845
                  a 15.9155 15.9155 0 0 1 0 31.831
                  a 15.9155 15.9155 0 0 1 0 -31.831"
              />
            </svg>
            <span ref={timerNumberRef} className={styles.timerNumber}>
              3
            </span>
          </div>
        </div>
        <div className={styles.notificationText}>Ви видалили "{deletedItem.item.name}"</div>
        <button
          type="button"
          className={styles.restoreButton}
          onClick={handleRestoreItem}
          aria-label="Повернути товар"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.87402 11.375V11.375C5.87402 8.47788 3.52132 6.12569 0.624214 6.13346V6.13346"
              stroke="white"
              strokeWidth="1.5"
            />
            <path
              d="M5.875 0.875V0.875C5.875 3.77212 3.5223 6.12431 0.625191 6.11654V6.11654"
              stroke="white"
              strokeWidth="1.5"
            />
            <path d="M2.375 6.125L11.375 6.125" stroke="white" strokeWidth="1.5" />
          </svg>
          Повернути
        </button>
      </div>
    )
  })

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className={styles.overlay}
          onClick={onClose}
          role="button"
          tabIndex={-1}
          aria-label="Закрити корзину"
        />
      )}

      {/* Sidebar */}
      <div
        className={`${styles.sidebar} ${isOpen ? styles.open : ''} ${
          isAnimating ? styles.animating : ''
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cart-title"
        aria-describedby="cart-description"
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <header className={styles.header}>
          <div className={styles.titleSection}>
            <h2 id="cart-title" className={styles.title}>
              Кошик
            </h2>
            <div
              className={styles.itemCount}
              aria-label={`${cartStats.totalItems} товарів у корзині`}
            >
              {cartStats.totalItems}
            </div>
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Закрити корзину"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="28"
              height="28"
              viewBox="0 0 28 28"
              fill="none"
              aria-hidden="true"
            >
              <rect
                x="0.666016"
                y="0.666668"
                width="26.6667"
                height="26.6667"
                rx="8"
                stroke="#BFBFBF"
                strokeWidth="1.25"
              />
              <path
                d="M11.1713 16.8287L16.8281 11.1719"
                stroke="#BFBFBF"
                strokeWidth="1.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M11.1713 11.1719L16.8281 16.8288"
                stroke="#BFBFBF"
                strokeWidth="1.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </header>

        {/* Content */}
        <main className={styles.content} id="cart-description">
          {cartItems.length === 0 ? (
            <div className={styles.emptyCart}>
              <div className={styles.emptyCartIcon}>
                <img src="/images/empty-cart.svg" alt="empty-cart" width={208} height={220} />
              </div>
              <h3>Упс… Ваш кошик сумує без кави!</h3>
              <p>Не бачимо жодного товару в кошику, виправляй це скоріше</p>
              <MainButton text="В каталог" href="/shop" />
            </div>
          ) : (
            <>
              {/* Cart Items */}
              <div className={styles.cartItems} role="list" aria-label="Товари в корзині">
                {cartItems.map((item, index) => {
                  // Якщо цей товар видаляється, показуємо NotificationTimer замість нього
                  if (
                    deletedItem &&
                    deletedItem.item.id === item.id &&
                    deletedItem.item.variationId === item.variationId
                  ) {
                    return (
                      <div
                        key={`deleted-${item.id}-${item.variationId || index}`}
                        className={styles.cartItem}
                      >
                        <NotificationTimer />
                      </div>
                    )
                  }

                  return (
                    <CartItemComponent
                      key={`${item.id}-${item.variationId || index}`}
                      item={item}
                      onQuantityChange={handleQuantityChange}
                      onRemove={handleRemoveItem}
                    />
                  )
                })}
              </div>

              {/* Recommendations */}
              <RecommendationsSection
                recommendedProducts={recommendedProducts}
                loading={loadingRecommended}
                error={recommendedError}
                onAddToCart={handleAddRecommendedToCart}
              />
            </>
          )}
        </main>

        {/* Footer */}
        {cartItems.length > 0 && (
          <footer className={styles.footer}>
            {/* Total and Checkout */}
            <div className={styles.total}>
              <div className={styles.totalLabel}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  aria-hidden="true"
                >
                  <g clipPath="url(#clip0_2061_5535)">
                    <path
                      d="M12.758 11.2424H10.6369C9.30017 11.2424 8.21266 10.1549 8.21266 8.81823C8.21266 7.48155 9.30017 6.39404 10.6369 6.39404H12.758C12.9255 6.39404 13.0611 6.2585 13.0611 6.091V5.18192C13.0611 4.54648 12.5682 4.0297 11.9454 3.97959L10.2051 0.939864C10.0439 0.658732 9.78343 0.457798 9.47182 0.374362C9.16168 0.291494 8.83737 0.335016 8.55979 0.496576L2.59389 3.96981H1.5461C0.877612 3.96981 0.333984 4.51341 0.333984 5.18192V12.4545C0.333984 13.123 0.877584 13.6666 1.5461 13.6666H11.849C12.5175 13.6666 13.0611 13.123 13.0611 12.4545V11.5455C13.0611 11.378 12.9255 11.2424 12.758 11.2424ZM10.5809 2.81654L11.2412 3.96981H8.60004L10.5809 2.81654ZM3.79842 3.96981L8.86487 1.02035C9.00188 0.940148 9.16197 0.918841 9.31498 0.959693C9.46975 1.00111 9.59878 1.10114 9.67897 1.24111L9.6796 1.24222L4.99473 3.96981H3.79842Z"
                      fill="#0D0D0D"
                    />
                    <path
                      d="M12.7597 7.00012H10.6385C9.63587 7.00012 8.82031 7.81568 8.82031 8.81827C8.82031 9.82087 9.63587 10.6364 10.6385 10.6364H12.7597C13.2609 10.6364 13.6687 10.2286 13.6687 9.72735V7.9092C13.6687 7.4079 13.2609 7.00012 12.7597 7.00012ZM10.6385 9.42432C10.3044 9.42432 10.0324 9.15236 10.0324 8.81827C10.0324 8.48419 10.3044 8.21223 10.6385 8.21223C10.9725 8.21223 11.2445 8.48419 11.2445 8.81827C11.2445 9.15236 10.9726 9.42432 10.6385 9.42432Z"
                      fill="#0D0D0D"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_2061_5535">
                      <rect
                        width="13.3333"
                        height="13.3333"
                        fill="white"
                        transform="translate(0.333984 0.333313)"
                      />
                    </clipPath>
                  </defs>
                </svg>
                Всього
              </div>
              <div className={styles.totalPrice} aria-live="polite">
                {cartStats.totalPrice} ₴
              </div>
            </div>

            <button
              type="button"
              className={styles.checkoutButton}
              onClick={handleCheckout}
              aria-label={`Оформити замовлення на суму ${cartStats.totalPrice} ₴`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="15"
                viewBox="0 0 15 15"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M7.66797 0.168823V0.168823C7.66797 4.03165 10.8049 7.16791 14.6677 7.15755V7.15755"
                  stroke="white"
                  strokeWidth="1.5"
                />
                <path
                  d="M7.66797 14.166V14.166C7.66797 10.3032 10.8049 7.16693 14.6677 7.17729V7.17729"
                  stroke="white"
                  strokeWidth="1.5"
                />
                <path d="M12.334 7.16602L0.333984 7.16602" stroke="white" strokeWidth="1.5" />
              </svg>
              <span>
                Оформити замовлення
                <span className={styles.itemCountBadge}>({cartStats.totalItems})</span>
              </span>
            </button>
          </footer>
        )}
      </div>
    </>
  )
}

export default React.memo(CartSidebar)
